<script lang="ts">
  import { formatCurrency, formatDate } from '../../lib/analytics.js';
  import Badge from '../common/Badge.svelte';
  import Button from '../common/Button.svelte';
  import SearchableSelect from '../common/SearchableSelect.svelte';
  import Tooltip from '../common/Tooltip.svelte';
  import SplitTransactionModal from './SplitTransactionModal.svelte';
  import type { Transaction, TransactionEditValues } from '../../types/transaction';

  let {
    transaction,
    categories = [],
    isEditing = false,
    isSelected = false,
    editValues = $bindable({} as TransactionEditValues),
    updateAllSimilar = $bindable(true),
    similarCount = 0,
    onEdit,
    onSave,
    onCancel,
    onToggleSelection,
    onSplit,
    onUnsplit,
    onCreateCategory
  }: {
    transaction: Transaction;
    categories: string[];
    isEditing?: boolean;
    isSelected?: boolean;
    editValues?: TransactionEditValues;
    updateAllSimilar?: boolean;
    similarCount?: number;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    onToggleSelection: () => void;
    onSplit?: (splits: any[]) => void;
    onUnsplit?: () => void;
    onCreateCategory?: (categoryName: string) => Promise<void>;
  } = $props();

  let showSplitModal = $state(false);

  // Extract bank name from source path or filename
  function getSourceName(source: string | undefined): string {
    if (!source) return 'Unknown';

    // Handle paths like "Chase/Shelly/Chase Account Activity.CSV"
    // Extract just the first folder name
    const parts = source.split('/');
    let name = parts[0] || source;

    // Remove file extension (.csv, .CSV, etc.)
    name = name.replace(/\.(csv|CSV|txt|TXT)$/i, '');

    // Extract just the first word/part (before space, dash, or underscore)
    // e.g., "SoFi Savings Feb 21 2026" -> "SoFi"
    const firstPart = name.split(/[\s\-_]/)[0];

    return firstPart || name;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      onSave();
    } else if (event.key === 'Escape') {
      onCancel();
    }
  }

  function handleSplitClick() {
    showSplitModal = true;
  }

  function handleSplitConfirm(splits: any[]) {
    if (onSplit) {
      onSplit(splits);
    }
    showSplitModal = false;
  }

  function handleSplitCancel() {
    showSplitModal = false;
  }
</script>

<tr class:editing={isEditing} class:selected={isSelected}>
  <td class="checkbox-col">
    <input
      type="checkbox"
      checked={isSelected}
      onchange={onToggleSelection}
    />
  </td>
  <td>{formatDate(transaction.date)}</td>

  <td>
    {#if isEditing}
      <input
        type="text"
        bind:value={editValues.displayName}
        placeholder={transaction.description}
        onkeydown={handleKeydown}
      />
    {:else}
      <div class="description-cell">
        <span class="description-text">
          {transaction.displayName || transaction.description}
        </span>
        {#if transaction.displayName}
          <Tooltip text="Original: {transaction.description}" size="sm">
            <span class="original-desc">✏️</span>
          </Tooltip>
        {/if}
        {#if transaction.isRecurring}
          <span class="recurring-badge" title={`Recurring ${transaction.recurringType || 'transaction'}`}>
            {transaction.recurringType === 'subscription' ? '🔄' : transaction.recurringType === 'bill' ? '📄' : transaction.recurringType === 'income' ? '💰' : transaction.recurringType === 'transfer' ? '↔️' : '🔁'}
          </span>
        {/if}
        {#if transaction.excluded}
          <span class="excluded-badge" title={`Excluded from analytics (${transaction.exclusionReason || 'user'})`}>🚫</span>
        {/if}
      </div>
    {/if}
  </td>

  <td>
    {#if isEditing}
      <SearchableSelect
        bind:value={editValues.category}
        options={categories}
        placeholder="Search categories..."
        onkeydown={handleKeydown}
        onCreate={onCreateCategory}
      />
    {:else}
      <Badge variant="category">{transaction.category}</Badge>
    {/if}
  </td>

  <td>
    <Badge variant="info">{getSourceName(transaction.source) || transaction.bank || 'Unknown'}</Badge>
  </td>

  <td class:negative={transaction.amount < 0} class:positive={transaction.amount > 0}>
    {formatCurrency(transaction.amount)}
  </td>

  <td>
    {#if isEditing}
      <input
        type="text"
        bind:value={editValues.person}
        placeholder="Person"
        onkeydown={handleKeydown}
      />
    {:else}
      <Badge variant="info">{transaction.person || 'Unknown'}</Badge>
    {/if}
  </td>

  <td class="actions">
    {#if isEditing}
      <span class="editing-indicator">Editing...</span>
    {:else}
      <div class="action-buttons">
        <Button variant="primary" size="sm" onclick={onEdit}>Edit</Button>
        {#if (transaction.isSplit || transaction.splitParentId) && onUnsplit}
          <Button variant="warning" size="sm" onclick={onUnsplit}>Unsplit</Button>
        {/if}
      </div>
    {/if}
  </td>
</tr>

{#if isEditing}
  <tr class="edit-panel-row">
    <td colspan="8">
      <div class="edit-panel">
        <div class="edit-panel-content">
          <div class="edit-options">
            <label class="edit-option">
              <input type="checkbox" bind:checked={editValues.isRecurring} />
              <span>Recurring</span>
            </label>

            {#if editValues.isRecurring}
              <select bind:value={editValues.recurringType} class="recurring-type-select">
                <option value="">Type...</option>
                <option value="subscription">Subscription</option>
                <option value="bill">Bill</option>
                <option value="income">Income</option>
                <option value="transfer">Transfer</option>
              </select>

              <div class="recurring-interval-input">
                <input
                  type="number"
                  bind:value={editValues.recurringInterval}
                  placeholder="Days"
                  min="1"
                  class="interval-days-input"
                  title="Recurring interval in days (e.g., 30 for monthly, 365 for yearly)"
                />
                <span class="interval-label">days</span>
              </div>
            {/if}

            <label class="edit-option">
              <input type="checkbox" bind:checked={editValues.excluded} />
              <span>Exclude from Analytics</span>
            </label>

            {#if !transaction.splitParentId && !transaction.isSplit && onSplit}
              <Button variant="ghost" size="sm" onclick={handleSplitClick}>Split Transaction</Button>
            {/if}
            {#if (transaction.isSplit || transaction.splitParentId) && onUnsplit}
              <Button variant="warning" size="sm" onclick={onUnsplit}>Unsplit</Button>
            {/if}

            <div class="button-spacer"></div>

            <Button variant="secondary" size="sm" onclick={onCancel}>Cancel</Button>
            <Button variant="success" size="sm" onclick={onSave}>Save</Button>
          </div>

          {#if similarCount > 1}
            <label class="update-toggle">
              <input type="checkbox" bind:checked={updateAllSimilar} />
              <span>Update all {similarCount} similar transactions</span>
            </label>
          {/if}
        </div>
      </div>
    </td>
  </tr>
{/if}

{#if showSplitModal}
  <SplitTransactionModal
    {transaction}
    {categories}
    onSplit={handleSplitConfirm}
    onCancel={handleSplitCancel}
  />
{/if}

<style lang="scss">
  tr {
    &:hover {
      background: var(--color-background-secondary);
    }

    &.editing {
      background: var(--color-warning-light);
    }

    &.selected {
      background: var(--color-primary-light);

      &:hover {
        background: var(--color-primary-border);
      }
    }
  }

  td {
    padding: var(--spacing-sm) var(--spacing-lg);
    border-bottom: 1px solid var(--color-border);
    font-size: var(--font-size-sm);
  }

  .checkbox-col {
    width: 40px;
    text-align: center;
    padding: var(--spacing-sm) !important;

    input[type="checkbox"] {
      width: var(--icon-size-xs);
      height: var(--icon-size-xs);
      cursor: pointer;
      margin: 0;
    }
  }

  /* Column widths */
  td:nth-child(2) { /* Date */
    width: 110px;
    white-space: nowrap;
  }


  td:nth-child(5) { /* Source */
    width: 80px;
   
  }

  td:nth-child(8) { /* Actions */
    width: 80px;
  }

  .negative {
    color: var(--color-danger);
    font-weight: var(--font-weight-semibold);
  }

  .positive {
    color: var(--color-success);
    font-weight: var(--font-weight-semibold);
  }

  .actions {
    white-space: nowrap;
  }

  .editing-indicator {
    font-size: var(--font-size-sm);
    color: var(--color-warning);
    font-weight: var(--font-weight-medium);
  }

  .action-buttons {
    display: flex;
    gap: var(--spacing-xs);
  }

  .edit-panel-row {
    background: var(--color-warning-light);
    border-top: 2px solid var(--color-warning);

    td {
      padding: 0 !important;
    }
  }

  .edit-panel {
    padding: var(--spacing-lg);
  }

  .edit-panel-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .edit-options {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg);
    flex-wrap: wrap;
  }

  .button-spacer {
    flex: 1;
    min-width: 40px;
  }

  .edit-option {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    font-size: var(--font-size-sm);
    cursor: pointer;
    user-select: none;

    input[type="checkbox"] {
      cursor: pointer;
      width: var(--icon-size-xs);
      height: var(--icon-size-xs);
      margin: 0;
    }

    span {
      font-weight: var(--font-weight-medium);
      color: var(--color-text-secondary);
    }
  }

  .recurring-type-select {
    padding: var(--spacing-sm) var(--spacing-sm);
    border: 1px solid var(--color-border-medium);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    background: white;

    &:focus {
      outline: none;
      border-color: var(--color-primary-bright);
    }
  }

  .recurring-interval-input {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }

  .interval-days-input {
    width: 70px;
    padding: var(--spacing-sm) var(--spacing-sm);
    border: 1px solid var(--color-border-medium);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);

    &:focus {
      outline: none;
      border-color: var(--color-primary-bright);
    }
  }

  .interval-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
  }

  .update-toggle {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
    cursor: pointer;
    user-select: none;
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--color-warning-light);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-warning);
    transition: all var(--transition-fast);

    input[type="checkbox"] {
      cursor: pointer;
      width: var(--icon-size-xs);
      height: var(--icon-size-xs);
      margin: 0;
    }

    span {
      font-weight: var(--font-weight-medium);
    }

    &:hover {
      background: var(--color-warning-border);
      color: var(--color-text-primary);
    }
  }

  input, select {
    width: 100%;
    padding: var(--spacing-sm);
    border: 1px solid var(--color-border-medium);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);

    &:focus {
      outline: none;
      border-color: var(--color-primary-bright);
    }
  }

  .description-cell {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    flex-wrap: nowrap;
    max-width: 100%;
    overflow: hidden;
  }

  .description-text {
    flex: 1;
    min-width: 180px;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .original-desc {
    position: relative;
    font-size: var(--font-size-sm);
    opacity: 0.7;
    cursor: help;
    flex-shrink: 0;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }

  .recurring-badge {
    font-size: var(--font-size-base);
    cursor: help;
    flex-shrink: 0;
    opacity: 0.9;
  }

  .excluded-badge {
    font-size: var(--font-size-base);
    cursor: help;
    flex-shrink: 0;
    opacity: 0.7;
  }

    @media (max-width: 1280px) {
   
       td, tr {
      padding: var(--spacing-xs) var(--spacing-xs);
    }
  }


  /* Tablet portrait and below - hide less important columns */
  @media (max-width: 768px) {
    td:nth-child(7), /* Person */
    td:nth-child(5) { /* Source */
      display: none;
    }

    .edit-panel-row td {
      display: table-cell !important;
    }

    .description-text {
      min-width: 150px;
    }
  }

  /* Large phones and below */
  @media (max-width: 640px) {
    td {
      font-size: var(--font-size-sm);
      padding: var(--spacing-sm) var(--spacing-sm);
    }

    .checkbox-col {
      width: 32px;

      input[type="checkbox"] {
        width: var(--icon-size-xs);
        height: var(--icon-size-xs);
      }
    }

    .action-buttons {
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    .description-text {
      min-width: 120px;
      font-size: var(--font-size-xs);
    }

    .edit-options {
      gap: var(--spacing-sm);
    }

    .edit-panel {
      padding: var(--spacing-md);
    }
  }

  /* Small phones */
  @media (max-width: 480px) {
    .description-cell {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-xs);
    }

    .edit-options {
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
    }

    .button-spacer {
      display: none;
    }
  }
</style>
